import { useState } from 'react';
import { Play, Pause, Heart, MoreHorizontal } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { hapticMedium, hapticLight } from '@/lib/haptics';
import { useAuth } from '@/components/AuthProvider';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Audiobook, AudiobookPage } from '@/hooks/use-audiobooks';
import { AudiobookSection } from './AudiobookSection';
import { TrackOptionsSheet } from './TrackOptionsSheet';
import { motion } from 'framer-motion';
import type { RoxboxTrack as ContextRoxboxTrack } from '@/contexts/RoxboxPlayerContext';

interface RoxboxTrack {
  id: string;
  name: string;
  artist: string | null;
  cover_image_url: string;
  audio_url: string;
  duration_seconds: number | null;
  video_url: string | null;
  display_order: number | null;
  is_active: boolean | null;
}

interface MobileTrackHomeProps {
  tracks: RoxboxTrack[];
  audiobooks: Audiobook[];
  currentTrack: RoxboxTrack | null;
  currentAudiobookPage: AudiobookPage | null;
  selectedAudiobookId: number | null;
  isPlaying: boolean;
  onSelectTrack: (track: RoxboxTrack) => void;
  onOpenAudiobook: (audiobook: Audiobook) => void;
  onTogglePlay: () => void;
  onOpenNowPlaying: () => void;
  onShowAuthModal: () => void;
  onShowPaywall: () => void;
}

function formatDuration(seconds: number | null): string {
  if (!seconds) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function getTimeBasedGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

export function MobileTrackHome({
  tracks,
  audiobooks,
  currentTrack,
  currentAudiobookPage,
  selectedAudiobookId,
  isPlaying,
  onSelectTrack,
  onOpenAudiobook,
  onTogglePlay,
  onOpenNowPlaying,
  onShowAuthModal,
  onShowPaywall,
}: MobileTrackHomeProps) {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [optionsTrack, setOptionsTrack] = useState<ContextRoxboxTrack | null>(null);

  // Fetch liked tracks
  const { data: likedTrackIds = [] } = useQuery({
    queryKey: ['likedTracks', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('liked_tracks')
        .select('track_id')
        .eq('user_id', user.id);
      if (error) throw error;
      return data.map(lt => lt.track_id);
    },
    enabled: !!user,
  });

  const likeMutation = useMutation({
    mutationFn: async (trackId: string) => {
      if (!user) return;
      await supabase.from('liked_tracks').insert({ user_id: user.id, track_id: trackId });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['likedTracks', user?.id] }),
  });

  const unlikeMutation = useMutation({
    mutationFn: async (trackId: string) => {
      if (!user) return;
      await supabase.from('liked_tracks').delete().eq('user_id', user.id).eq('track_id', trackId);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['likedTracks', user?.id] }),
  });

  const toggleLike = (trackId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) return;
    hapticLight();
    if (likedTrackIds.includes(trackId)) unlikeMutation.mutate(trackId);
    else likeMutation.mutate(trackId);
  };

  const isLiked = (trackId: string) => likedTrackIds.includes(trackId);
  const likedTracks = tracks.filter(t => likedTrackIds.includes(t.id));

  // Featured items
  const featuredTrack = tracks.length > 0 ? tracks[0] : null;
  const featuredAudiobook = audiobooks.length > 0 
    ? [...audiobooks].sort((a, b) => (b.reviewCount || 0) - (a.reviewCount || 0))[0]
    : null;

  const handleOpenAudiobook = (audiobook: Audiobook) => {
    if (!user) {
      onShowAuthModal();
      return;
    }
    if (audiobook.pages.length === 0) return;
    onOpenAudiobook(audiobook);
  };

  const isTrackPlaying = !!currentTrack;
  const isAudiobookPlaying = !!currentAudiobookPage;
  const miniPlayerImage = currentTrack?.cover_image_url || currentAudiobookPage?.imageUrl || '';
  const miniPlayerTitle = currentTrack?.name || (currentAudiobookPage ? `Page ${currentAudiobookPage.pageNumber}` : '');
  const miniPlayerSubtitle = currentTrack?.artist || currentAudiobookPage?.bookTitle || '';

  return (
    <div 
      className="min-h-screen flex flex-col bg-background"
      style={{ paddingTop: 'calc(var(--safe-top) + 0.75rem)' }}
    >
      {/* Floating Mini Player (mobile only) */}
      {isMobile && (isTrackPlaying || isAudiobookPlaying) && (
        <motion.div 
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="fixed left-0 right-0 z-50 px-4"
          style={{ top: 'calc(var(--safe-top) + 0.75rem)' }}
        >
          <button
            onClick={onOpenNowPlaying}
            className="w-full p-2.5 rounded-xl bg-white/10 backdrop-blur-md border border-white/5 flex items-center gap-3 active:scale-[0.98] transition-transform"
            style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
          >
            <div className="relative w-10 h-10 shrink-0 rounded-lg overflow-hidden">
              <img src={miniPlayerImage} alt={miniPlayerTitle} className="w-full h-full object-cover" />
              {isPlaying && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <div className="flex items-end gap-[2px] h-3">
                    <span className="w-[2px] bg-white rounded-full animate-pulse" style={{ height: '40%' }} />
                    <span className="w-[2px] bg-white rounded-full animate-pulse" style={{ height: '80%', animationDelay: '0.1s' }} />
                    <span className="w-[2px] bg-white rounded-full animate-pulse" style={{ height: '60%', animationDelay: '0.2s' }} />
                  </div>
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-white font-medium text-sm truncate">{miniPlayerTitle}</p>
              <p className="text-white/40 text-xs truncate">{miniPlayerSubtitle || 'Unknown'}</p>
            </div>
            <div
              onClick={(e) => {
                e.stopPropagation();
                hapticMedium();
                onTogglePlay();
              }}
              className="w-9 h-9 rounded-full bg-white flex items-center justify-center shrink-0 active:scale-90 transition-transform"
              style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
              role="button"
              tabIndex={0}
            >
              {isPlaying ? (
                <Pause className="w-4 h-4 text-black" fill="currentColor" />
              ) : (
                <Play className="w-4 h-4 text-black ml-0.5" fill="currentColor" />
              )}
            </div>
          </button>
        </motion.div>
      )}

      {/* Header - 10px margin-top to test Safari glass sampling theory */}
      <header 
        className={cn("px-5 pb-3", (isMobile && (isTrackPlaying || isAudiobookPlaying)) ? "pt-16" : "pt-2")}
        style={{ marginTop: '10px' }}
      >
        <h1 className="text-white font-bold text-2xl tracking-tight">
          {getTimeBasedGreeting()}
        </h1>
      </header>

      <ScrollArea className="flex-1">
        <div className="pb-32">
          {/* Featured Grid - Two cards side by side */}
          {(featuredTrack || featuredAudiobook) && (
            <div className="px-5 py-3">
              <div className="grid grid-cols-2 gap-3">
                {featuredTrack && (
                  <motion.button
                    whileTap={{ scale: 0.97 }}
                    onClick={() => onSelectTrack(featuredTrack)}
                    className="relative rounded-lg overflow-hidden aspect-square"
                  >
                    <img 
                      src={featuredTrack.cover_image_url} 
                      alt={featuredTrack.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <span className="text-[10px] font-semibold text-primary uppercase tracking-wider">Featured</span>
                      <p className="text-white font-semibold text-sm truncate mt-0.5">{featuredTrack.name}</p>
                    </div>
                    {currentTrack?.id === featuredTrack.id && isPlaying && (
                      <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                        <div className="flex items-end gap-[1px] h-2.5">
                          <span className="w-[2px] bg-white rounded-full animate-pulse" style={{ height: '50%' }} />
                          <span className="w-[2px] bg-white rounded-full animate-pulse" style={{ height: '100%', animationDelay: '0.15s' }} />
                          <span className="w-[2px] bg-white rounded-full animate-pulse" style={{ height: '70%', animationDelay: '0.3s' }} />
                        </div>
                      </div>
                    )}
                  </motion.button>
                )}

                {featuredAudiobook && (
                  <motion.button
                    whileTap={{ scale: 0.97 }}
                    onClick={() => handleOpenAudiobook(featuredAudiobook)}
                    className="relative rounded-lg overflow-hidden aspect-square"
                  >
                    <img 
                      src={featuredAudiobook.coverUrl} 
                      alt={featuredAudiobook.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <span className="text-[10px] font-semibold text-orange-400 uppercase tracking-wider">Audiobook</span>
                      <p className="text-white font-semibold text-sm truncate mt-0.5">{featuredAudiobook.title}</p>
                    </div>
                  </motion.button>
                )}
              </div>
            </div>
          )}

          {/* Liked Songs */}
          {likedTracks.length > 0 && (
            <div className="py-4">
              <h2 className="text-white font-semibold text-base px-5 mb-3 flex items-center gap-2">
                <Heart className="w-4 h-4 text-primary" fill="currentColor" />
                Liked Songs
              </h2>
              <div className="flex gap-3 overflow-x-auto pb-1 pl-5 pr-3 scrollbar-none">
                {likedTracks.map((track, index) => (
                  <motion.button
                    key={track.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2, delay: index * 0.03 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => onSelectTrack(track)}
                    className="shrink-0 w-28"
                  >
                    <div className="relative aspect-square rounded-lg overflow-hidden mb-2">
                      <img src={track.cover_image_url} alt={track.name} className="w-full h-full object-cover" />
                      {currentTrack?.id === track.id && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          {isPlaying ? (
                            <div className="flex items-end gap-0.5 h-5">
                              <span className="w-1 bg-white rounded-full animate-pulse" style={{ height: '60%' }} />
                              <span className="w-1 bg-white rounded-full animate-pulse" style={{ height: '100%', animationDelay: '0.15s' }} />
                              <span className="w-1 bg-white rounded-full animate-pulse" style={{ height: '50%', animationDelay: '0.3s' }} />
                            </div>
                          ) : (
                            <Play className="w-6 h-6 text-white" fill="currentColor" />
                          )}
                        </div>
                      )}
                    </div>
                    <p className="text-white text-sm font-medium truncate">{track.name}</p>
                    <p className="text-white/40 text-xs truncate">{track.artist || 'Unknown'}</p>
                  </motion.button>
                ))}
              </div>
            </div>
          )}

          {/* All Tracks - Cleaner list */}
          <div className="px-5 pt-2">
            <h2 className="text-white/60 font-semibold text-xs uppercase tracking-wider mb-3">
              All Tracks
            </h2>
            <div className="space-y-0.5">
              {tracks.map((track) => {
                const isCurrentTrack = currentTrack?.id === track.id;
                const trackIsLiked = isLiked(track.id);
                
                return (
                  <motion.button
                    key={track.id}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => onSelectTrack(track)}
                    className={cn(
                      "w-full flex items-center gap-3 p-2.5 rounded-lg transition-colors",
                      isCurrentTrack ? "bg-white/10" : "active:bg-white/5"
                    )}
                  >
                    {/* Cover with playing indicator */}
                    <div className="relative w-11 h-11 shrink-0 rounded-md overflow-hidden">
                      <img src={track.cover_image_url} alt={track.name} className="w-full h-full object-cover" />
                      {isCurrentTrack && isPlaying && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <div className="flex items-end gap-[2px] h-3">
                            <span className="w-[2px] bg-primary rounded-full animate-pulse" style={{ height: '50%' }} />
                            <span className="w-[2px] bg-primary rounded-full animate-pulse" style={{ height: '100%', animationDelay: '0.15s' }} />
                            <span className="w-[2px] bg-primary rounded-full animate-pulse" style={{ height: '70%', animationDelay: '0.3s' }} />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0 text-left">
                      <p className={cn(
                        "font-medium text-sm truncate",
                        isCurrentTrack ? "text-primary" : "text-white"
                      )}>
                        {track.name}
                      </p>
                      <p className="text-white/40 text-xs truncate">
                        {track.artist || 'Unknown Artist'}
                      </p>
                    </div>

                    {/* Like button */}
                    {user && (
                      <button
                        onClick={(e) => toggleLike(track.id, e)}
                        className={cn(
                          "p-1.5 transition-colors shrink-0",
                          trackIsLiked ? "text-primary" : "text-white/20"
                        )}
                      >
                        <Heart className="w-4 h-4" fill={trackIsLiked ? "currentColor" : "none"} />
                      </button>
                    )}

                    {/* More options button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        hapticLight();
                        setOptionsTrack(track as ContextRoxboxTrack);
                      }}
                      className="p-1.5 text-white/20 active:text-white/50 transition-colors shrink-0 active:scale-90"
                    >
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  </motion.button>
                );
              })}

              {tracks.length === 0 && (
                <div className="text-center py-12 text-white/30">
                  <p className="text-base">No tracks yet</p>
                  <p className="text-sm mt-1">Check back soon!</p>
                </div>
              )}
            </div>
          </div>

          {/* Audiobooks */}
          <AudiobookSection
            audiobooks={audiobooks}
            currentPage={currentAudiobookPage}
            isPlaying={isPlaying}
            selectedAudiobookId={selectedAudiobookId}
            onSelectAudiobook={handleOpenAudiobook}
          />
        </div>
      </ScrollArea>
      
      {/* Track Options Sheet */}
      <TrackOptionsSheet 
        open={!!optionsTrack} 
        onOpenChange={(open) => !open && setOptionsTrack(null)} 
        track={optionsTrack}
        onSubscribe={onShowPaywall}
      />
    </div>
  );
}
